import React, { forwardRef, useContext } from "react";
import { ClavaContext } from "../../../config/contexts";
import type { TranslatorKeys } from "../../../config/translator";
import { translate } from "../../../config/translator";
import type { Translation, TranslationCreate } from "../../../client/api";

type RoundedRectangleProps = {
  hasTitle?: boolean;
  titleType?: "upLeft" | "centerLeft" | "upCenter";
  titleKey?: TranslatorKeys | Translation | TranslationCreate;
  titleSuffix?: React.ReactNode;
  titleClickable?: boolean;
  onTitleClick?: () => void;
  hasBorder?: boolean;
  id?: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: () => void;
};

const RoundedRectangle = forwardRef<HTMLDivElement, RoundedRectangleProps>(
  (
    {
      hasTitle,
      titleType,
      titleKey,
      titleSuffix,
      titleClickable,
      onTitleClick,
      hasBorder,
      id,
      className,
      children,
      onClick
    },
    ref
  ) => {
    const { l } = useContext(ClavaContext);

    const baseClasses = "rounded-xl";
    const computedClassName = [
      baseClasses,
      className || "",
      hasBorder ? "border" : ""
    ]
      .filter(Boolean)
      .join(" ");

    const titleBaseClasses = "text-base font-medium text-font dark:text-font-dark text-left";
    const titleFullWidth = titleType === "centerLeft" || titleType === "upCenter";

    const content = (
      <>
        {hasTitle && titleKey ? (
          <div className="pt-4 px-4 w-full">
            {titleClickable && onTitleClick ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onTitleClick();
                }}
                className={`${titleBaseClasses} hover:text-primary dark:hover:text-primary transition-colors ${titleFullWidth ? "w-full" : ""}`}
              >
                {translate(titleKey, l)}{titleSuffix}
              </button>
            ) : (
              <h6 className={titleBaseClasses}>
                {translate(titleKey, l)}{titleSuffix}
              </h6>
            )}
          </div>
        ) : null}
        {children}
      </>
    );

    const isInteractive = typeof onClick === "function";
    if (isInteractive) {
      return (
        <button id={id} className={computedClassName} onClick={onClick} type="button">
          {content}
        </button>
      );
    }
    return (
      <div ref={ref} id={id} className={computedClassName}>
        {content}
      </div>
    );
  }
);

RoundedRectangle.displayName = "RoundedRectangle";

export default RoundedRectangle;
